import { supabase } from '@/integrations/supabase/client';

export interface VideoSummaryResult {
  url: string;
  transcript: string;
  summary: string; // full structured summary markdown
  model: string;
  generatedAt: string;
}

function buildSystemPrompt(): string {
  return [
    'You are an expert video note-taker and executive summarizer.',
    'Summarize the entire video end-to-end (not just the intro). Produce a clean, well-structured brief that is easy to skim and act on.',
    'Follow strict rules:',
    '- Always respond in English but if full transcript is in other language then respond in that language mixing with English.',
    '- Be concise but complete. Prefer bullets over long paragraphs.',
    '- Include a short overview and key takeaways; add action items if applicable.',
    '- Do NOT include or summarize any self-promotion, sponsorships, ads, affiliate mentions, coupons, merch, course plugs, subscribe/like/share requests, or channel promos. Omit them entirely.',
    '- If the transcript contains long promotional segments, skip them. Only focus on the educational/technical/content substance.',
    '- Use Markdown headings (##, ###), bullet lists, numbered lists, bold (**text**) and italic (*text*) for emphasis.',
    '- Use inline code for technical terms or commands when relevant.',
    '- Never include the raw transcript in the summary body.',
    '- If the transcript appears partial/low-quality, state limitations politely and still summarize what is clear.',
    '- Avoid hallucinations: do not add facts not present in transcript.',
    '',
    'Output format:',
    '## Overview:',
    '*2-4 sentence executive summary of the entire video.*',
    '',
    '## Key Takeaways:',
    '- 5-10 bullets focusing on core insights, concrete tips, or definitions. Use **bold** for key terms.',
    '',
    'Action Items:',
    '- Clear, actionable bullets',
  ].join('\n');
}

export async function videoSummaryService(videoUrl: string): Promise<VideoSummaryResult> {
  if (!videoUrl || !/^https?:\/\//i.test(videoUrl)) {
    throw new Error('Please provide a valid video URL (e.g., a YouTube link).');
  }
  // Call Supabase Edge function to handle transcript + summarization with server-side key fallback
  const { data, error } = await supabase.functions.invoke('runAgent', {
    body: { agent: 'video-summary', url: videoUrl },
  });
  if (error) {
    throw new Error(error.message || 'Video summary service failed');
  }

  // Expect shape from edge fn: { url, transcript, summary, model, generatedAt }
  const result = data as VideoSummaryResult | any;
  if (!result || !result.summary) {
    throw new Error('Invalid response from runAgent video-summary function');
  }
  return {
    url: result.url || videoUrl,
    transcript: result.transcript || '',
    summary: result.summary,
    model: result.model || 'gpt-oss-120b',
    generatedAt: result.generatedAt || new Date().toISOString(),
  };
}

const escapeHtml = (v: string) =>
  v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Minimal, safe markdown -> HTML converter for headings, lists, code, links, emphasis
function markdownToHtml(md: string): string {
  if (!md) return '';

  // Preserve fenced code blocks first
  const codeBlocks: string[] = [];
  md = md.replace(/```([\s\S]*?)```/g, (_m, p1) => {
    codeBlocks.push(p1);
    return `[[[CODE_BLOCK_${codeBlocks.length - 1}]]]`;
  });

  // Escape HTML before injecting tags
  md = escapeHtml(md);

  // Inline code
  md = md.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Bold and italic
  md = md.replace(/\*\*([^*]+)\*\*/g, '<strong>$1<\/strong>');
  md = md.replace(/\*([^*]+)\*/g, '<em>$1<\/em>');

  // Links [text](url)
  md = md.replace(/\[([^\]]+)\]\((https?:[^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1<\/a>');

  // Split into lines for block-level processing
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let inUl = false;
  let inOl = false;

  const flushLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  for (let rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushLists();
      out.push('');
      continue;
    }

    // Headings
    if (/^###\s+/.test(line)) { flushLists(); out.push('<h3>' + line.replace(/^###\s+/, '') + '</h3>'); continue; }
    if (/^##\s+/.test(line))  { flushLists(); out.push('<h2>' + line.replace(/^##\s+/, '') + '</h2>'); continue; }
    if (/^#\s+/.test(line))   { flushLists(); out.push('<h1>' + line.replace(/^#\s+/, '') + '</h1>'); continue; }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      if (inUl) { out.push('</ul>'); inUl = false; }
      if (!inOl) { out.push('<ol>'); inOl = true; }
      out.push('<li>' + line.replace(/^\d+\.\s+/, '') + '</li>');
      continue;
    }

    // Unordered list
    if (/^(?:-|\*)\s+/.test(line)) {
      if (inOl) { out.push('</ol>'); inOl = false; }
      if (!inUl) { out.push('<ul>'); inUl = true; }
      out.push('<li>' + line.replace(/^(?:-|\*)\s+/, '') + '</li>');
      continue;
    }

    // Paragraph
    flushLists();
    out.push('<p>' + line + '</p>');
  }

  flushLists();

  let html = out.join('\n');

  // Restore code blocks
  html = html.replace(/\[\[\[CODE_BLOCK_(\d+)\]\]\]/g, (_m, idx) => {
    const code = escapeHtml(codeBlocks[Number(idx)] || '');
    return `<pre><code>${code}</code></pre>`;
  });

  return html;
}

export function formatVideoSummaryResponse(
  result: VideoSummaryResult,
  isDarkMode: boolean = false
): string {
  const wrapper = isDarkMode ? 'border-slate-800 text-slate-100' : 'border-slate-200 text-slate-900';
  const headerBg = isDarkMode ? 'bg-violet-500/10 border-violet-400/30' : 'bg-violet-50 border-violet-200';
  const metaText = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  const safeUrl = escapeHtml(result.url);

  return `
<div class="border ${wrapper} rounded-2xl overflow-hidden" data-video-summary>
  <div class="${headerBg} border-b p-4 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <div>
        <p class="text-[11px] font-semibold tracking-[0.35em] uppercase opacity-70">Video Summary</p>
      </div>
    </div>
  </div>
  <div class="prose prose-sm dark:prose-invert px-4 py-5">${markdownToHtml(result.summary)}</div>
  <div class="px-4 pb-4" data-transcript-section>
    <div class="flex items-center justify-between">
      <button
        type="button"
        class="text-xs ${metaText} cursor-pointer inline-flex items-center gap-1 hover:opacity-80 select-none"
        data-transcript-toggle
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 transition-transform duration-200 transform -rotate-90">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
        <span class="toggle-text">Show raw transcript</span>
      </button>
      <a href="${safeUrl}" target="_blank" rel="noopener" class="text-xs underline decoration-none">Open video</a>
    </div>
    <div class="transcript-content hidden mt-2 max-h-64 overflow-auto text-[11px] ${metaText} whitespace-pre-wrap">${escapeHtml(result.transcript)}</div>
  </div>
</div>`;
}
